import React, { memo, useCallback, useState } from 'react';
import { CloseRounded, FavoriteRounded, ForumRounded } from '@mui/icons-material';
import { batch, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import DirectInteractionActionCreator from 'actions/DirectInteractionActionCreator';
import NearbyActionCreator from 'actions/NearbyActionCreator';
import { isError } from 'models/core/error/IError';
import { EXPLORER_PATH, MESSAGES_PATH, REGISTER_USER } from 'models/Paths';
import { IRelation } from 'models/user/IRelation';
import { IStrangerUser } from 'models/user/IStrangerUser/IStrangerUser';
import { IUser } from 'models/user/IUser';
import { MatchDialog } from 'pages/Mainpage/components/MatchDialog/MatchDialog';
import ResourceService from 'services/i18n';
import useTranslation from 'services/i18n/core/useTranslation';
import ChatActionCreator from 'services/Chat/actions/ChatActionCreator';
import NotificationActionCreator from 'services/Notifications/actions/NotificationActionCreator';

export interface StrangerProfileActionButtonsProps {
    strangerUser: IStrangerUser;
    token?: string;
    user?: IUser;
    isLiked?: boolean;
}

export const StrangerProfileActionButtons = memo(({ strangerUser, token, user, isLiked = false }: StrangerProfileActionButtonsProps) => {
    const dispatch = useDispatch();
    const history = useHistory();
    const { LIKE_SUCCESS } = useTranslation();
    const [liked, setLiked] = useState(isLiked);
    const [isMatchModalOpen, setIsMatchModalOpen] = useState(false);

    const redirectToRegister = useCallback(() => {
        history.push(REGISTER_USER, { from: history.location.pathname });
    }, [history]);

    const handleDislike = useCallback(async () => {
        if (!user || !token) {
            redirectToRegister();
            return;
        }

        await DirectInteractionActionCreator.triggerStrangerUserRelation(strangerUser.Profilid, 'dislike', token, user);
        history.push(EXPLORER_PATH);
    }, [history, redirectToRegister, strangerUser.Profilid, token, user]);

    const handleMessage = useCallback(async () => {
        if (!user || !token) {
            redirectToRegister();
            return;
        }

        await dispatch(ChatActionCreator.createDialog(strangerUser.Username, strangerUser));
        history.push(`${MESSAGES_PATH.replace(':id?', strangerUser.Profilid)}?openChat=true`);
    }, [dispatch, history, redirectToRegister, strangerUser, token, user]);

    const handleLike = useCallback(async () => {
        if (!user || !token) {
            redirectToRegister();
            return;
        }

        const res = await DirectInteractionActionCreator.triggerStrangerUserRelation(strangerUser.Profilid, 'like', token, user);

        if (res.ok) {
            const [response] = (await res.json()) as [IRelation];
            setLiked(true);

            if (response.isMatch) {
                setIsMatchModalOpen(true);
            } else if (!isError(res) && (response as { Note?: string }).Note === 'Success') {
                batch(() => {
                    dispatch(NearbyActionCreator.likeStrangerUser(strangerUser.Profilid));
                    dispatch(
                        NotificationActionCreator.enqueueSnackbar({
                            key: `like-${strangerUser.Profilid}`,
                            message: ResourceService.replace(LIKE_SUCCESS, { name: strangerUser.Username }),
                            options: { variant: 'default' },
                        })
                    );
                });
            }
        }
    }, [LIKE_SUCCESS, dispatch, redirectToRegister, strangerUser, token, user]);

    return (
        <>
            <div className="stranger-profile__actions">
                <button type="button" className="stranger-profile__action-btn stranger-profile__action-btn--dislike" onClick={handleDislike} aria-label="Dislike">
                    <CloseRounded sx={{ color: '#F31F57', fontSize: 32 }} />
                </button>
                <button type="button" className="stranger-profile__action-btn stranger-profile__action-btn--message" onClick={handleMessage} aria-label="Message">
                    <ForumRounded sx={{ fontSize: 32 }} />
                </button>
                <button type="button" className="stranger-profile__action-btn stranger-profile__action-btn--like" onClick={handleLike} aria-label="Like">
                    <FavoriteRounded sx={{ color: liked ? '#F31F57' : '#2ecc71', fontSize: 32 }} />
                </button>
            </div>

            {isMatchModalOpen && (
                <MatchDialog open={isMatchModalOpen} onClose={() => setIsMatchModalOpen(false)} user={user} strangerUser={strangerUser} />
            )}
        </>
    );
});

export default StrangerProfileActionButtons;
